/**
 * Map Style Control for MapLibre GL
 *
 * Custom control that allows switching between map styles (Terrain, Satellite).
 * Positioned in the top-left corner of the map.
 */

import type maplibregl from 'maplibre-gl';
import type { StyleSpecification } from 'maplibre-gl';

interface MapStyleOption {
  id: string;
  name: string;
  style: string | StyleSpecification;
}

export class MapStyleControl {
  private map: maplibregl.Map | null = null;
  private container: HTMLElement | null = null;
  private styles: MapStyleOption[];
  private initialStyleId: string;
  private onStyleChanged: (() => void) | null;

  constructor(geoapifyApiKey: string, mapTilerApiKey: string, initialStyleId: string = 'TERRAIN', onStyleChanged: (() => void) | null = null) {
    this.initialStyleId = initialStyleId;
    this.onStyleChanged = onStyleChanged;
    this.styles = [
      {
        id: 'TERRAIN',
        name: 'Terrain',
        style: `https://maps.geoapify.com/v1/styles/klokantech-basic/style.json?apiKey=${geoapifyApiKey}`,
      },
      {
        id: 'SATELLITE',
        name: 'Satellite',
        style: {
          version: 8,
          sources: {
            satellite: {
              type: 'raster',
              tiles: [`https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${mapTilerApiKey}`],
              tileSize: 256,
              attribution: '© <a href="https://www.maptiler.com/">MapTiler</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
              maxzoom: 20,
            },
          },
          layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
        } as StyleSpecification,
      },
    ];
  }

  onAdd(map: maplibregl.Map): HTMLElement {
    this.map = map;
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group map-style-control';

    // Create button for each style
    this.styles.forEach((style) => {
      const button = document.createElement('button');
      button.className = 'map-style-button';
      button.textContent = style.name;
      button.title = `Switch to ${style.name} view`;

      button.addEventListener('click', () => {
        if (this.map) {
          this.map.setStyle(style.style as Parameters<maplibregl.Map['setStyle']>[0]);
          // Update active state
          this.container?.querySelectorAll('.map-style-button').forEach((btn) => {
            btn.classList.remove('active');
          });
          button.classList.add('active');
          // Re-draw overlays after the new style is fully settled.
          // 'idle' fires after style + tiles are loaded, ensuring sources/layers
          // can be safely added.
          if (this.onStyleChanged) {
            this.map.once('idle', () => { this.onStyleChanged!() });
          }
        }
      });

      if (this.container) {
        this.container.appendChild(button);
      }
    });

    // Mark the button matching the initial style as active
    const buttons = this.container.querySelectorAll<HTMLButtonElement>('.map-style-button');
    const activeIndex = this.styles.findIndex((s) => s.id === this.initialStyleId);
    const activeButton = buttons[activeIndex >= 0 ? activeIndex : 0];
    if (activeButton) {
      activeButton.classList.add('active');
    }

    return this.container;
  }

  onRemove(): void {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.map = null;
  }
}
